import { useState, useEffect, useRef, useCallback } from 'react';
import {
  X,
  Send,
  Loader2,
  Bot,
  User,
  Wrench,
  ChevronDown,
  ChevronUp,
  Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api, { ChatSession, ChatMessage } from '../../../../lib/api';
import { VoiceButton } from './VoiceButton';
import { useTranslation } from 'react-i18next';

// ─── Types ────────────────────────────────────────────────────────────────────

interface StockChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

// ─── Message Bubble ───────────────────────────────────────────────────────────

function MessageBubble({ message }: { message: ChatMessage }) {
  const { t } = useTranslation('stock');
  const [showTools, setShowTools] = useState(false);
  const isAssistant = message.role === 'assistant';
  const hasTools = !!(message.tool_calls && Array.isArray(message.tool_calls) && (message.tool_calls as unknown[]).length > 0);

  return (
    <div className={`flex gap-2 ${isAssistant ? 'flex-row' : 'flex-row-reverse'}`}>
      {/* Avatar */}
      <div
        className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5 ${
          isAssistant ? 'bg-indigo-100 dark:bg-indigo-900/30' : 'bg-gray-200'
        }`}
      >
        {isAssistant ? (
          <Bot className="w-4 h-4 text-indigo-600" />
        ) : (
          <User className="w-4 h-4 text-gray-600 dark:text-gray-300" />
        )}
      </div>

      {/* Bubble */}
      <div className={`max-w-[82%] flex flex-col gap-1 ${isAssistant ? 'items-start' : 'items-end'}`}>
        <div
          className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${
            isAssistant
              ? 'bg-white border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100 rounded-tl-sm shadow-sm'
              : 'bg-indigo-600 text-white rounded-tr-sm'
          }`}
        >
          <div className={isAssistant ? 'prose-sm' : ''}>
            {isAssistant ? (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  table: ({ children }) => (
                    <div className="overflow-x-auto my-2">
                      <table className="text-xs border-collapse w-full">{children}</table>
                    </div>
                  ),
                  thead: ({ children }) => <thead className="bg-indigo-50 dark:bg-indigo-900/20">{children}</thead>,
                  th: ({ children }) => (
                    <th className="border border-gray-200 dark:border-gray-700 px-2 py-1.5 text-left font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">{children}</th>
                  ),
                  td: ({ children }) => (
                    <td className="border border-gray-200 dark:border-gray-700 px-2 py-1.5 text-gray-700 dark:text-gray-300">{children}</td>
                  ),
                  tr: ({ children }) => <tr className="even:bg-gray-50 dark:bg-gray-700/50">{children}</tr>,
                  p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
                  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                  ul: ({ children }) => <ul className="list-disc ml-4 mb-1 space-y-0.5">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal ml-4 mb-1 space-y-0.5">{children}</ol>,
                  li: ({ children }) => <li>{children}</li>,
                  h3: ({ children }) => <h3 className="font-semibold text-gray-900 dark:text-gray-100 mt-2 mb-1">{children}</h3>,
                  h4: ({ children }) => <h4 className="font-medium text-gray-800 dark:text-gray-100 mt-1.5 mb-0.5">{children}</h4>,
                  hr: () => <hr className="my-2 border-gray-200 dark:border-gray-700" />,
                  code: ({ children }) => <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs font-mono">{children}</code>,
                }}
              >
                {message.content}
              </ReactMarkdown>
            ) : (
              message.content
            )}
          </div>
        </div>

        {/* Tool calls badge */}
        {hasTools && (
          <button
            onClick={() => setShowTools(!showTools)}
            className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <Wrench className="w-3 h-3" />
            <span>{t('stockChat.toolsUsed', { count: (message.tool_calls as unknown[]).length })}</span>
            {showTools ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        )}

        {showTools && hasTools && (
          <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg p-2 text-xs text-gray-600 dark:text-gray-300 w-full max-w-full">
            {(message.tool_calls as Array<{ name: string; input: Record<string, unknown> }>).map((call, i) => (
              <div key={i} className="flex items-center gap-1.5 py-0.5">
                <span className="font-mono bg-gray-200 px-1 rounded text-gray-700 dark:text-gray-300">{call.name}</span>
                {Object.keys(call.input || {}).length > 0 && (
                  <span className="text-gray-400 dark:text-gray-500 truncate">
                    {JSON.stringify(call.input).slice(0, 60)}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        <span className="text-[10px] text-gray-400 dark:text-gray-500 px-1">
          {new Date(message.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    </div>
  );
}

// ─── Session List ─────────────────────────────────────────────────────────────

function SessionList({
  sessions,
  onSelect,
  onNew,
  onDelete,
}: {
  sessions: ChatSession[];
  onSelect: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
}) {
  const { t } = useTranslation('stock');
  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b">
        <button
          onClick={onNew}
          className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          {t('stockChat.newConversation')}
        </button>
      </div>
      <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
        {sessions.length === 0 && (
          <div className="p-6 text-center text-sm text-gray-400 dark:text-gray-500">
            {t('stockChat.noConversations')}
          </div>
        )}
        {sessions.map((s) => (
          <div key={s.id} className="group flex items-center gap-1 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 dark:bg-gray-700/50 transition-colors">
            <button
              onClick={() => onSelect(s.id)}
              className="flex-1 text-left min-w-0"
            >
              <div className="font-medium text-sm text-gray-800 dark:text-gray-100 truncate">{s.title || t('stockChat.defaultTitle')}</div>
              <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                {s.messages_count} messages ·{' '}
                {new Date(s.last_message_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}{' '}
                {new Date(s.last_message_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </div>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(s.id); }}
              className="flex-shrink-0 p-1.5 rounded-md text-gray-400 dark:text-gray-500 hover:text-red-500 hover:bg-red-50 dark:bg-red-900/20 opacity-0 group-hover:opacity-100 transition-all"
              title={t('stockChat.deleteTitle')}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Chat Panel ──────────────────────────────────────────────────────────

const CHAT_WIDTH_KEY = 'stock_chat_width';
const MIN_WIDTH = 320;
const MAX_WIDTH = 900;
const DEFAULT_WIDTH = 420;

export function StockChatPanel({ isOpen, onClose }: StockChatPanelProps) {
  const { t, i18n } = useTranslation('stock');
  const [view, setView] = useState<'sessions' | 'chat'>('chat');
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [panelWidth, setPanelWidth] = useState<number>(() => {
    const saved = localStorage.getItem(CHAT_WIDTH_KEY);
    return saved ? Math.min(Math.max(Number(saved), MIN_WIDTH), MAX_WIDTH) : DEFAULT_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const resizeStartX = useRef<number>(0);
  const resizeStartWidth = useRef<number>(DEFAULT_WIDTH);

  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = panelWidth;
    setIsResizing(true);
  }, [panelWidth]);

  useEffect(() => {
    if (!isResizing) return;
    const onMove = (e: MouseEvent) => {
      const delta = resizeStartX.current - e.clientX;
      const newWidth = Math.min(Math.max(resizeStartWidth.current + delta, MIN_WIDTH), MAX_WIDTH);
      setPanelWidth(newWidth);
    };
    const onUp = () => {
      setIsResizing(false);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      setPanelWidth(w => { localStorage.setItem(CHAT_WIDTH_KEY, String(w)); return w; });
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isResizing]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (view === 'chat' && isOpen) scrollToBottom();
  }, [messages, view, isOpen, scrollToBottom]);

  useEffect(() => {
    if (isOpen && !currentSessionId) {
      startNewSession();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, currentSessionId]);


  async function startNewSession() {
    setIsCreatingSession(true);
    setMessages([]);
    setCurrentSessionId(null);
    try {
      const res = await api.createChatSession();
      if (res.success && res.data) {
        setCurrentSessionId(res.data.session.id);
        setMessages([res.data.welcome]);
        setView('chat');
      }
    } catch {
      toast.error(t('stockChat.startError'));
    } finally {
      setIsCreatingSession(false);
    }
  }

  async function loadSession(sessionId: string) {
    setIsLoading(true);
    try {
      const res = await api.getChatHistory(sessionId);
      if (res.success && res.data) {
        setCurrentSessionId(sessionId);
        setMessages(res.data.messages);
        setView('chat');
      }
    } catch {
      toast.error(t('stockChat.loadError'));
    } finally {
      setIsLoading(false);
    }
  }

  async function loadSessions() {
    try {
      const res = await api.listChatSessions();
      if (res.success && res.data) {
        setSessions(res.data.sessions);
        setView('sessions');
      }
    } catch {
      toast.error(t('stockChat.loadListError'));
    }
  }

  async function handleDeleteSession(sessionId: string) {
    try {
      await api.deleteChatSession(sessionId);
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      if (currentSessionId === sessionId) {
        setCurrentSessionId(null);
        setMessages([]);
      }
    } catch {
      toast.error(t('stockChat.deleteError'));
    }
  }

  async function handleSend() {
    const content = inputValue.trim();
    if (!content || !currentSessionId || isLoading) return;

    setInputValue('');
    setIsLoading(true);

    // Optimistically add user message
    const tempUserMsg: ChatMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUserMsg]);

    try {
      const res = await api.sendChatMessage(currentSessionId, content, i18n.language);
      if (res.success && res.data) {
        // Replace temp message with real one + add assistant message
        setMessages((prev) => [
          ...prev.filter((m) => m.id !== tempUserMsg.id),
          res.data!.userMessage,
          res.data!.assistantMessage,
        ]);
        // Notify CartPage to reload if mounted
        window.dispatchEvent(new CustomEvent('cart-updated'));
      } else {
        toast.error(t('stockChat.sendError'));
        setMessages((prev) => prev.filter((m) => m.id !== tempUserMsg.id));
      }
    } catch {
      toast.error(t('stockChat.connectionError'));
      setMessages((prev) => prev.filter((m) => m.id !== tempUserMsg.id));
    } finally {
      setIsLoading(false);
    }
  }

  const handleVoiceAudio = useCallback(async (blob: Blob) => {
    if (!currentSessionId || isLoading) return;

    setIsLoading(true);
    const tempId = `temp-voice-${Date.now()}`;
    const tempMsg: ChatMessage = {
      id: tempId,
      role: 'user',
      content: '🎙️ Transcription en cours…',
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempMsg]);

    try {
      const res = await api.sendVoiceMessage(currentSessionId, blob);
      if (res.success && res.data) {
        setMessages((prev) => [
          ...prev.filter((m) => m.id !== tempId),
          res.data!.userMessage,
          res.data!.assistantMessage,
        ]);
        window.dispatchEvent(new CustomEvent('cart-updated'));
      } else {
        toast.error(t('stockChat.voiceError'));
        setMessages((prev) => prev.filter((m) => m.id !== tempId));
      }
    } catch {
      toast.error(t('stockChat.connectionError'));
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
    } finally {
      setIsLoading(false);
    }
  }, [currentSessionId, isLoading]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  // Suggestion chips for quick questions
  const SUGGESTIONS = [
    t('stockChat.suggestions.ruptures'),
    t('stockChat.suggestions.order'),
    t('stockChat.suggestions.expiring'),
    t('stockChat.suggestions.stockValue'),
    t('stockChat.suggestions.anomalies'),
  ];

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/10 z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className="fixed right-0 top-0 h-full bg-white shadow-2xl z-50 flex flex-col border-l border-gray-200 dark:border-gray-700"
        style={{ width: panelWidth }}
      >
        {/* Resize handle */}
        <div
          onMouseDown={startResize}
          className={`absolute left-0 top-0 h-full w-1.5 cursor-col-resize group z-10 hover:bg-indigo-400/40 transition-colors ${isResizing ? 'bg-indigo-400/40' : ''}`}
          title={t('stockChat.resizeTitle')}
        >
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-10 rounded-full bg-gray-300 group-hover:bg-indigo-400 transition-colors opacity-0 group-hover:opacity-100" />
        </div>
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-indigo-600 text-white">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <Bot className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm">{t('stockChat.title')}</div>
            <div className="text-xs text-indigo-200">Powered by Claude</div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={loadSessions}
              title={t('stockChat.historyTitle')}
              className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
              </svg>
            </button>
            {currentSessionId && (
              <button
                onClick={startNewSession}
                title={t('stockChat.newChatTitle')}
                className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        {view === 'sessions' ? (
          <SessionList
            sessions={sessions}
            onSelect={loadSession}
            onNew={startNewSession}
            onDelete={handleDeleteSession}
          />
        ) : (
          <>
            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-gray-50 dark:bg-gray-700/50">
              {isCreatingSession && (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
                </div>
              )}

              {messages.map((msg) => (
                <MessageBubble key={msg.id} message={msg} />
              ))}

              {isLoading && (
                <div className="flex gap-2">
                  <div className="w-7 h-7 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Bot className="w-4 h-4 text-indigo-600" />
                  </div>
                  <div className="bg-white border border-gray-200 dark:border-gray-700 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                    <div className="flex gap-1 items-center h-4">
                      <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce [animation-delay:0ms]" />
                      <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce [animation-delay:150ms]" />
                      <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce [animation-delay:300ms]" />
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Suggestion chips (show only when few messages) */}
            {messages.length <= 1 && !isLoading && (
              <div className="px-3 py-2 border-t border-gray-100 dark:border-gray-700 bg-white">
                <div className="flex gap-1.5 flex-wrap">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => {
                        setInputValue(s);
                        inputRef.current?.focus();
                      }}
                      className="text-xs px-2.5 py-1 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 rounded-full border border-indigo-200 hover:bg-indigo-100 dark:bg-indigo-900/30 transition-colors whitespace-nowrap"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input bar */}
            <div className="px-3 py-3 border-t border-gray-200 dark:border-gray-700 bg-white">
              <div className="flex items-end gap-2 bg-gray-100 dark:bg-gray-700 rounded-xl px-3 py-2">
                <textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('stockChat.inputPlaceholder')}
                  rows={1}
                  className="flex-1 bg-transparent text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 resize-none outline-none max-h-28 leading-relaxed py-0.5"
                  style={{ minHeight: '24px' }}
                  disabled={isLoading || isCreatingSession || !currentSessionId}
                />
                <VoiceButton
                  onAudioReady={handleVoiceAudio}
                  disabled={isLoading || isCreatingSession || !currentSessionId}
                />
                <button
                  onClick={handleSend}
                  disabled={!inputValue.trim() || isLoading || isCreatingSession || !currentSessionId}
                  className="flex-shrink-0 w-8 h-8 rounded-lg bg-indigo-600 text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:bg-indigo-700 transition-colors"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>
              <p className="text-[10px] text-gray-400 dark:text-gray-500 text-center mt-1.5">
                {t('stockChat.inputHint')}
              </p>
            </div>
          </>
        )}
      </div>

    </>
  );
}
